import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import {
  CommunityImageUpload,
  createCommunity,
  getCommunityByID,
  updateCommunity,
} from "./services/community.services";
import { clearCommunityDetails } from "./communitySlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import { ITour, ITourDate } from "../tour/tourModel";
import {
  getAllActiveTours,
  getTourDatesByTourID,
} from "../tour/services/tour.services";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import { getAllActiveActivities } from "../activity/service/activity.services";
import { IActivity } from "../activity/activityModel";
import moment from "moment";

interface ChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const TourChangeWatcher: React.FC<ChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const dispatch = useAppDispatch();
  const TourID = formRenderProps.valueGetter("TourID");

  useEffect(() => {
    if (TourID) {
      dispatch(getTourDatesByTourID(TourID));
    }
  }, [TourID]);
  return null;
};

const CreateCommunity: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const CommunityID = location.state?.CommunityID;

  const CommunityDetail = useAppSelector(
    (state) => state.community.CommunityDetail,
  );
  const loading = useAppSelector((state) => state.community.loading);
  const TourList = useAppSelector((state) => state.tour.TourList);
  const ActivityList = useAppSelector((state) => state.activity.ActivityList);
  const TourDateDetails = useAppSelector((state) => state.tour.TourDateDetails);

  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (CommunityID) {
      dispatch(getCommunityByID(CommunityID));
    }
    return () => {
      dispatch(clearCommunityDetails());
    };
  }, [CommunityID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [CommunityDetail, CommunityDetail?.Date]);

  useEffect(() => {
    dispatch(getAllActiveTours());
    dispatch(getAllActiveActivities());
  }, []);

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const CommunityImage =
      values?.CommunityImage && typeof values?.CommunityImage !== "string"
        ? values.CommunityImage[0].getRawFile()
        : "";
    const PersonImage =
      values?.PersonImage && typeof values?.PersonImage !== "string"
        ? values.PersonImage[0].getRawFile()
        : "";

    try {
      if (CommunityID) {
        const editPayload = {
          ID: CommunityID,
          PersonName: values?.PersonName ? values?.PersonName : "",
          TourID: values?.TourID ? +values?.TourID : null,
          Title: values?.Title ? values?.Title : "",
          LongDescription: values?.LongDescription
            ? values?.LongDescription
            : "",
          ShortDescription: values?.ShortDescription
            ? values?.ShortDescription
            : "",
          Description: values?.Description ? values?.Description : "",
          InstaProfile: values?.InstaProfile ? values?.InstaProfile : "",
          FacebookProfile: values?.FacebookProfile
            ? values?.FacebookProfile
            : "",
          LinkedInProfile: values?.LinkedInProfile
            ? values?.LinkedInProfile
            : "",
          TwitterProfile: values?.TwitterProfile ? values?.TwitterProfile : "",
          WebsiteProfile: values?.WebsiteProfile ? values?.WebsiteProfile : "",
          TotalSeats: values?.TotalSeats ? +values?.TotalSeats : null,
          GroupSize: values?.GroupSize ? +values?.GroupSize : null,
          Date: values?.Date ? values?.Date : null,
          ActivityIDs: values?.ActivityIDs
            ? values?.ActivityIDs?.join(",")
            : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateCommunity(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.PersonImage && typeof values?.PersonImage !== "string") ||
            (values?.CommunityImage &&
              typeof values?.CommunityImage !== "string")
          ) {
            ImageData.append("ID", CommunityID);
            ImageData.append("PersonImage", PersonImage);
            ImageData.append("CommunityImage", CommunityImage);
            await dispatch(CommunityImageUpload(ImageData));
          }
          dispatch(clearCommunityDetails());
          navigate(-1);
        }
      } else {
        const insertPayload = {
          PersonName: values?.PersonName ? values?.PersonName : "",
          TourID: values?.TourID ? +values?.TourID : null,
          Title: values?.Title ? values?.Title : "",
          LongDescription: values?.LongDescription
            ? values?.LongDescription
            : "",
          ShortDescription: values?.ShortDescription
            ? values?.ShortDescription
            : "",
          Description: values?.Description ? values?.Description : "",
          InstaProfile: values?.InstaProfile ? values?.InstaProfile : "",
          FacebookProfile: values?.FacebookProfile
            ? values?.FacebookProfile
            : "",
          LinkedInProfile: values?.LinkedInProfile
            ? values?.LinkedInProfile
            : "",
          TwitterProfile: values?.TwitterProfile ? values?.TwitterProfile : "",
          WebsiteProfile: values?.WebsiteProfile ? values?.WebsiteProfile : "",
          TotalSeats: values?.TotalSeats ? +values?.TotalSeats : null,
          GroupSize: values?.GroupSize ? +values?.GroupSize : null,
          Date: values?.Date ? values?.Date : null,
          ActivityIDs: values?.ActivityIDs
            ? values?.ActivityIDs?.join(",")
            : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createCommunity(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            ((values?.PersonImage && typeof values?.PersonImage !== "string") ||
              (values?.CommunityImage &&
                typeof values?.CommunityImage !== "string")) &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("ID", response?.payload?.Data?.ID);
            ImageData.append("PersonImage", PersonImage);
            ImageData.append("CommunityImage", CommunityImage);
            await dispatch(CommunityImageUpload(ImageData));
          }
          dispatch(clearCommunityDetails());
          navigate("/community");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={CommunityDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <TourChangeWatcher formRenderProps={formRenderProps} />
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {CommunityID ? "Update Community" : "Add Community"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="Title"
                      label="Title"
                      placeholder="Title"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="PersonName"
                      label="Creator Name"
                      placeholder="i.e. John Doe"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="Description"
                      label="Creator Description"
                      placeholder="Creator Description"
                      component={FormRichTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      id={"TourID"}
                      name={"TourID"}
                      label={"Tour"}
                      placeholder="Tour"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={TourList?.map((tour: ITour) => {
                        return {
                          value: tour?.ID,
                          label: tour?.TourName,
                        };
                      })}
                      onChange={() => {
                        formRenderProps.onChange("Date", {
                          value: "",
                        });
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`Date`}
                      label="Date"
                      placeholder="Date"
                      // format="dd/MM/yyyy"
                      component={FormSelectionField}
                      options={TourDateDetails?.map((tour: ITourDate) => {
                        return {
                          value: `${moment(tour?.TourDate).format(
                            "YYYY-MM-DD",
                          )}`,
                          label: `${moment(tour?.TourDate).format(
                            "DD/MM/YYYY",
                          )}`,
                        };
                      })}
                      onChange={(e: any) => {
                        if (e.value) {
                          const selectedDate: any = TourDateDetails?.find(
                            (tour) =>
                              moment(tour?.TourDate).isSame(moment(e.value)),
                          );
                          if (selectedDate) {
                            formRenderProps.onChange("TotalSeats", {
                              value: selectedDate?.NoOfSeat,
                            });
                            formRenderProps.onChange("GroupSize", {
                              value: selectedDate?.NoOfSeat,
                            });
                          } else {
                            formRenderProps.onChange("TotalSeats", {
                              value: "",
                            });
                            formRenderProps.onChange("GroupSize", {
                              value: "",
                            });
                          }
                        } else {
                          formRenderProps.onChange("TotalSeats", {
                            value: "",
                          });
                          formRenderProps.onChange("GroupSize", {
                            value: "",
                          });
                        }
                      }}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem className="d-flex gap-2">
                    <Field
                      wrapperClassName="right-aligned-field w-100"
                      name="TotalSeats"
                      label="Total Seats"
                      placeholder="0"
                      min={0}
                      max={1000}
                      format="n0"
                      component={FormNumericTextField}
                      disabled={true}
                    />
                    <Field
                      wrapperClassName="right-aligned-field w-100"
                      name="GroupSize"
                      label="Group Size"
                      placeholder="0"
                      min={0}
                      max={1000}
                      format="n0"
                      component={FormNumericTextField}
                      disabled={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      id={"ActivityIDs"}
                      name={"ActivityIDs"}
                      label={"Activities"}
                      placeholder="Select Activities"
                      component={FormMultiSelectionFiled}
                      // validator={requiredArrayValidator}
                      // astrike={true}
                      options={ActivityList?.map((activity: IActivity) => {
                        return {
                          value: activity?.ID,
                          label: activity?.ActivityName,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="InstaProfile"
                      label="Insta Profile URL"
                      placeholder="Insta Profile URL"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="FacebookProfile"
                      label="Facebook Profile URL"
                      placeholder="Facebook Profile URL"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="LinkedInProfile"
                      label="Linked In Profile URL"
                      placeholder="Linked In Profile URL"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="TwitterProfile"
                      label="Twitter Profile URL"
                      placeholder="Twitter Profile URL"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="WebsiteProfile"
                      label="Website URL"
                      placeholder="Website URL"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      placeholder="i.e. Active"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3} className="d-flex gap-2">
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="ShortDescription"
                      label="Short Description"
                      placeholder="Short Description"
                      component={FormRichTextField}
                    />
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="LongDescription"
                      label="Long Description"
                      placeholder="Long Description"
                      component={FormRichTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3} className="d-flex gap-2">
                    <Field
                      name="PersonImage"
                      type="file"
                      accept="image/*"
                      label="Creator Image"
                      component={FormImageUpload}
                    />
                    {/* </GridLayoutItem>
                  <GridLayoutItem> */}
                    <Field
                      name="CommunityImage"
                      type="file"
                      accept="image/*"
                      label="Community Image"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={CommunityID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />

                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/community")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateCommunity;
